/*
 * Complete Your Look (PDP micro-upsell)
 * ------------------------------------------------------------------------------------------------
 * Handles color switching for the recommended product rendered by snippets/complete-your-look.liquid.
 * Adding to cart is handled by the theme's native <form is="product-form"> (theme.js), so the cart
 * drawer, cart count and a11y announcements work exactly like the main Add to Cart button.
 */
(() => {
  if (window.customElements.get('complete-your-look')) {
    return;
  }

  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    const value = Number(cents) / 100;

    const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2);
      }
    });
  };

  class CompleteYourLook extends HTMLElement {
    connectedCallback() {
      this._form = document.forms[this.getAttribute('form')];
      this._options = this._parseOptions();
      this._productCache = new Map();
      this._abortController = new AbortController();

      // Standard radio-group keyboard behaviour: Tab lands on the checked swatch, arrows move between colors.
      this._radios.forEach((radio) => radio.removeAttribute('tabindex'));

      this.addEventListener('change', this._onChange.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get _radios() {
      return Array.from(this.querySelectorAll('.complete-your-look__swatches input[type="radio"]'));
    }

    _parseOptions() {
      try {
        return JSON.parse(this.querySelector('[data-cyl-options]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    async _onChange(event) {
      const input = event.target;

      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') {
        return;
      }

      this._syncSelectedSwatch(input);

      let data = this._options[input.value];

      if (!data) {
        return;
      }

      // Combined listing: the color lives on another product, fetch it once.
      if (!data.variantId && data.productUrl) {
        data = await this._resolveFromProductUrl(data);

        if (!data || !input.checked) {
          return; // request failed, or the shopper already picked another color
        }
      }

      this._applyVariant(data);
    }

    _syncSelectedSwatch(input) {
      this.querySelectorAll('.complete-your-look__swatches .color-swatch').forEach((swatch) => {
        swatch.classList.toggle('is-selected', swatch.getAttribute('for') === input.id);
      });
    }

    async _resolveFromProductUrl(data) {
      const productUrl = data.productUrl.split('?')[0];

      if (!this._productCache.has(productUrl)) {
        this._productCache.set(productUrl, fetch(`${productUrl}.js`).then((response) => response.ok ? response.json() : null).catch(() => null));
      }

      const product = await this._productCache.get(productUrl);

      if (!product) {
        return null;
      }

      const variant = product.variants.find((item) => item.available) || product.variants[0];
      const image = variant.featured_image?.src || product.featured_image;

      return {
        ...data,
        variantId: variant.id,
        available: variant.available,
        price: formatMoney(variant.price),
        compareAtPrice: variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : null,
        mediaId: variant.featured_media?.id || null,
        imageSrc: image ? (image.startsWith('//') ? `https:${image}` : image) : null,
        url: `${productUrl}?variant=${variant.id}`,
        title: product.title
      };
    }

    _applyVariant(data) {
      // 1. Variant submitted to the cart
      const idInput = this._form?.elements.namedItem('id');

      if (idInput) {
        idInput.value = data.variantId;
      }

      // 2. Button state
      const buyButtons = this.querySelector('buy-buttons');
      const button = buyButtons?.querySelector('button[type="submit"]');

      if (button) {
        const label = data.available ? buyButtons.dataset.addLabel : buyButtons.dataset.soldOutLabel;
        const content = button.contentElement || button;
        const title = data.title || this.dataset.productTitle;

        button.disabled = !data.available;
        button.classList.toggle('button--subdued', !data.available);
        button.setAttribute('aria-label', `${label}: ${title}`);
        content.textContent = label;
      }

      // 3. Price
      const price = this.querySelector('[data-cyl-price]');

      if (price && data.price) {
        price.replaceChildren();
        const current = document.createElement('span');
        current.textContent = data.price;
        price.append(current);

        if (data.compareAtPrice) {
          const compare = document.createElement('s');
          compare.className = 'complete-your-look__compare-price';
          compare.textContent = data.compareAtPrice;
          compare.setAttribute('aria-label', `${this.dataset.regularPriceLabel || ''} ${data.compareAtPrice}`.trim());
          price.append(' ', compare);
        }
      }

      // 4. Links + title (title can differ when colors are separate products)
      if (data.url) {
        this.querySelectorAll('[data-cyl-link]').forEach((link) => link.setAttribute('href', data.url));
      }

      const titleElement = this.querySelector('[data-cyl-title]');

      if (titleElement && data.title && titleElement.textContent !== data.title) {
        titleElement.textContent = data.title;
      }

      // 5. Image
      this._showImage(data);
    }

    _showImage(data) {
      const container = this.querySelector('.complete-your-look__media');

      if (!container) {
        return;
      }

      const images = Array.from(container.querySelectorAll('img'));
      let target = data.mediaId ? images.find((image) => image.dataset.mediaId === String(data.mediaId)) : null;

      if (!target && data.imageSrc) {
        target = images.find((image) => image.dataset.cylDynamic === data.imageSrc);

        if (!target) {
          const separator = data.imageSrc.includes('?') ? '&' : '?';
          target = document.createElement('img');
          target.className = 'complete-your-look__image';
          target.alt = '';
          target.dataset.cylDynamic = data.imageSrc;
          target.sizes = '(min-width: 1000px) 260px, 40vw';
          target.srcset = [300, 400, 600, 800].map((width) => `${data.imageSrc}${separator}width=${width} ${width}w`).join(', ');
          target.src = `${data.imageSrc}${separator}width=600`;
          target.hidden = true;
          container.append(target);
          images.push(target);
        }
      }

      if (!target) {
        return; // no dedicated image for this color: keep the current one
      }

      images.forEach((image) => {
        image.hidden = image !== target;
      });
    }
  }

  window.customElements.define('complete-your-look', CompleteYourLook);
})();